package structs

import kotlinx.coroutines.await
import kotlin.js.Promise as JsPromise
import kotlinx.serialization.json.Json
import org.khronos.webgl.Uint8Array
import org.ttt.autogenesis.network.RpcJson
import org.khronos.webgl.Int8Array
import org.khronos.webgl.get
import kotlin.js.Promise

@JsModule("jszip")
@JsNonModule
external class JSZip {
    fun file(name: String, data: Any): JSZip
    fun file(name: String): JSZipObject?
    fun generateAsync(options: dynamic): Promise<Any>

    companion object {
        fun loadAsync(data: Any): Promise<JSZip>
    }
}

external class JSZipObject {
    fun async(type: String): Promise<Any>
}

actual object MapPackManager {
    actual suspend fun pack(imageName: String, imageBytes: ByteArray, mapData: MapData): ByteArray {
        console.log("[MPM] pack: writingAgentConfig.ruleCategories.size=${mapData.writingAgentConfig.ruleCategories.size}")
        mapData.writingAgentConfig.ruleCategories.forEachIndexed { index, cat ->
            console.log("[MPM] pack: ruleCategory[$index] '${cat.name}': chancePercent=${cat.chancePercent}, rules=${cat.rules.size}")
        }
        val packData = MapPackData(imageName, mapData)
        val jsonString = RpcJson.encodeToString(MapPackData.serializer(), packData)

        // Operator directive (2026-08-14): every map image MUST be downsampled
        // before the safety agent sees it. The 256K-token budget is enforced
        // here on the client; the server gate's downsample is the safety net.
        val downsampledImageBytes = downsampleImageBytesForPack(imageBytes)

        val zip = JSZip()
        zip.file("map.json", jsonString)
        zip.file(imageName, downsampledImageBytes.toUint8Array())

        val options = js("{}")
        options.type = "uint8array"

        val uint8Array = zip.generateAsync(options).await() as Uint8Array
        return uint8Array.toByteArray()
    }

    actual suspend fun unpack(packBytes: ByteArray): UnpackedMapPack {
        // Convert to a typed Uint8Array first — JSZip's `loadAsync`
        // accepts the typed-array view directly (verified in the live
        // probe of 2026-08-13).
        val uint8Array = packBytes.toUint8Array()
        val zip = JSZip.loadAsync(uint8Array).await()

        val jsonFile = zip.file("map.json")
            ?: throw MapPackUnpackException("No map.json entry found in zip")
        val jsonString = jsonFile.async("string").await() as String
        console.log("[MPM] unpack: map.json size=${jsonString.length} chars")
        val mapPackData = try {
            RpcJson.decodeFromString(MapPackData.serializer(), jsonString)
        } catch (e: Exception) {
            throw MapPackUnpackException(
                "Map.json is not a valid MapPackData (${e.message ?: e::class.simpleName})"
            )
        }
        console.log("[MPM] unpack: mapPackData.mapData.writingAgentConfig.ruleCategories.size=${mapPackData.mapData.writingAgentConfig.ruleCategories.size}")
        mapPackData.mapData.writingAgentConfig.ruleCategories.forEachIndexed { index, cat ->
            console.log("[MPM] unpack: ruleCategory[$index] '${cat.name}': chancePercent=${cat.chancePercent}, rules=${cat.rules.size}")
        }

        val imageFile = zip.file(mapPackData.imageName)
            ?: throw MapPackUnpackException("No image entry found in zip (expected entry named '${mapPackData.imageName}')")
        val imageUint8Array = imageFile.async("uint8array").await() as Uint8Array
        val imageBytes = imageUint8Array.toByteArray()

        return UnpackedMapPack(
            imageName = mapPackData.imageName,
            imageBytes = imageBytes,
            mapData = mapPackData.mapData
        )
    }

    /**
     * Client-side downsample — enforces the 256K-token safety budget
     * before the zip payload is sent to the server.
     *
     * Operator directive (2026-08-14): every map image must be downsampled
     * before it reaches the safety agent. NO EXCEPTIONS.
     *
     * Mirrors the JVM `MapPackManager.downsampleImageBytesForPack` algorithm:
     *  1. Decode pixel dimensions from the PNG/JPEG header. If already
     *     <= 408 KB AND longest edge <= 1024 px, return as-is.
     *  2. Otherwise, iterate halving the longest edge (1024 -> 512 -> 256
     *     -> 128 -> 64) and re-encode as PNG via canvas.toDataURL().
     *  3. Return the first re-encode that fits the cap, or the smallest
     *     reachable re-encode (server gate's hard cap catches it).
     */
    internal suspend fun downsampleImageBytesForPack(sourceBytes: ByteArray): ByteArray {
        val capBytes = (256_000 / 0.627).toInt() // ~408 KB
        val maxDim = 1024
        val minDim = 64

        if (sourceBytes.size <= capBytes) {
            val (w, h) = try {
                decodeImageDimensions(sourceBytes)
            } catch (e: Throwable) {
                return sourceBytes
            }
            if (maxOf(w, h) <= maxDim) return sourceBytes
        }

        var currentMaxDim = maxDim
        var lastEncoded: ByteArray = sourceBytes
        while (currentMaxDim >= minDim) {
            val encoded = try {
                encodeImageAtMaxDim(sourceBytes, currentMaxDim)
            } catch (e: Throwable) {
                console.warn("[MPM] downsample pass at $currentMaxDim px failed: ${e.message}; returning prior bytes")
                return lastEncoded
            }
            lastEncoded = encoded
            if (encoded.size <= capBytes) {
                console.log("[MPM] downsample: image -> ${currentMaxDim}px = ${encoded.size} bytes (under cap)")
                return encoded
            }
            console.log("[MPM] downsample: image at ${currentMaxDim}px = ${encoded.size} bytes (over cap ${capBytes}); halving")
            currentMaxDim /= 2
        }
        console.warn("[MPM] downsample: reached ${minDim}px floor at ${lastEncoded.size} bytes (still over ${capBytes}); server gate will reject")
        return lastEncoded
    }

    /**
     * Decode PNG or JPEG dimensions from the raw header bytes. Returns
     * Pair(width, height). Throws on unsupported formats.
     */
    private fun decodeImageDimensions(bytes: ByteArray): Pair<Int, Int> {
        if (bytes.size >= 24 &&
            bytes[0] == 0x89.toByte() && bytes[1] == 0x50.toByte() &&
            bytes[2] == 0x4E.toByte() && bytes[3] == 0x47.toByte()) {
            val w = ((bytes[16].toInt() and 0xff) shl 24) or ((bytes[17].toInt() and 0xff) shl 16) or
                    ((bytes[18].toInt() and 0xff) shl 8) or (bytes[19].toInt() and 0xff)
            val h = ((bytes[20].toInt() and 0xff) shl 24) or ((bytes[21].toInt() and 0xff) shl 16) or
                    ((bytes[22].toInt() and 0xff) shl 8) or (bytes[23].toInt() and 0xff)
            return Pair(w, h)
        }
        if (bytes.size >= 4 && bytes[0] == 0xFF.toByte() && bytes[1] == 0xD8.toByte()) {
            var i = 2
            while (i < bytes.size - 9) {
                if (bytes[i] != 0xFF.toByte()) { i++; continue }
                val marker = bytes[i + 1].toInt() and 0xff
                if (marker == 0xC0 || marker == 0xC2) {
                    val h = ((bytes[i + 5].toInt() and 0xff) shl 8) or (bytes[i + 6].toInt() and 0xff)
                    val w = ((bytes[i + 7].toInt() and 0xff) shl 8) or (bytes[i + 8].toInt() and 0xff)
                    return Pair(w, h)
                }
                val segLen = ((bytes[i + 2].toInt() and 0xff) shl 8) or (bytes[i + 3].toInt() and 0xff)
                i += 2 + segLen
            }
        }
        throw RuntimeException("unable to decode image dimensions (not PNG or JPEG)")
    }

    /**
     * Decode the image, draw to a max-longest-edge canvas, re-encode as
     * PNG, return the encoded bytes. Async via Promise.
     */
    private suspend fun encodeImageAtMaxDim(sourceBytes: ByteArray, maxLongestEdge: Int): ByteArray {
        val promise: JsPromise<ByteArray> = JsPromise { resolve, reject ->
            val u8 = sourceBytes.toUint8Array()
            val buf = u8.asDynamic().buffer
            val blob = org.w3c.files.Blob(
                arrayOf(buf),
                org.w3c.files.BlobPropertyBag(type = "image/png")
            )
            val url = org.w3c.dom.url.URL.createObjectURL(blob)
            val img = org.w3c.dom.Image()
            img.onload = {
                try {
                    val srcW = img.naturalWidth
                    val srcH = img.naturalHeight
                    val longest = maxOf(srcW, srcH)
                    val scale = maxLongestEdge.toDouble() / longest.toDouble()
                    val dstW = (srcW * scale).toInt().coerceAtLeast(1)
                    val dstH = (srcH * scale).toInt().coerceAtLeast(1)
                    val canvas = kotlinx.browser.document.createElement("canvas") as org.w3c.dom.HTMLCanvasElement
                    canvas.width = dstW
                    canvas.height = dstH
                    val ctx = canvas.getContext("2d") as org.w3c.dom.CanvasRenderingContext2D
                    ctx.imageSmoothingEnabled = true
                    // imageSmoothingQuality is a string-only DOM enum in
                    // browsers; the Kotlin/JS binding types it as a union
                    // enum. Use js() to bypass the type check.
                    js("ctx.imageSmoothingQuality = 'high'")
                    ctx.drawImage(img, 0.0, 0.0, dstW.toDouble(), dstH.toDouble())
                    val pngDataUrl = canvas.toDataURL("image/png")
                    val base64 = pngDataUrl.substringAfter("base64,", missingDelimiterValue = "")
                    val encoded = decodeBase64ToBytes(base64)
                    org.w3c.dom.url.URL.revokeObjectURL(url)
                    resolve(encoded)
                } catch (e: dynamic) {
                    org.w3c.dom.url.URL.revokeObjectURL(url)
                    reject(e.unsafeCast<Throwable>())
                }
            }
            img.onerror = { _event: dynamic, _source: String, _lineno: Int, _colno: Int, _error: Any? ->
                org.w3c.dom.url.URL.revokeObjectURL(url)
                reject(RuntimeException("image load failed"))
            }
            img.src = url
        }
        return promise.await()
    }

    /**
     * Decode a base64 string into a byte array using window.atob.
     */
    private fun decodeBase64ToBytes(b64: String): ByteArray {
        val bin = kotlinx.browser.window.atob(b64)
        val out = ByteArray(bin.length)
        for (i in 0 until bin.length) {
            out[i] = (bin[i].code).toByte()
        }
        return out
    }
}

private fun ByteArray.toUint8Array(): Uint8Array
{
    // Allocate a fresh ArrayBuffer and copy bytes into it. The previous
    // implementation called `Uint8Array(this.buffer, ...)` which
    // creates a view over the underlying buffer — but a Kotlin
    // ByteArray on the JS target is a plain `Array<Number>`, so
    // `this.buffer` is `undefined`. JSZip's `loadAsync` then sees a
    // bad typed-array view and throws "Can't find end of central
    // directory" (observed in the catalogue thumbnail renderer's
    // live probe of 2026-08-13).
    //
    // Copying byte-by-byte with an explicit `and 0xff` mask also
    // converts signed `Byte` values (range -128..127) into the
    // unsigned 0..255 range that typed-array slots expect.
    val arr = Uint8Array(this.size)
    for (i in 0 until this.size)
    {
        arr.asDynamic()[i] = this[i].toInt() and 0xff
    }
    return arr
}

private fun Uint8Array.toByteArray(): ByteArray
{
    val arr = ByteArray(this.length)
    for (i in 0 until this.length)
    {
        arr[i] = this.asDynamic()[i].unsafeCast<Int>().toByte()
    }
    return arr
}
